import {
  Box,
  Chip,
  IconButton,
  Link as MuiLink,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined,
  InsertDriveFileOutlined,
} from "@mui/icons-material";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import AvatarStack from "components/common/AvatarStack";
import UserAvatar from "components/common/UserAvatar";
import { CALIBRATE_COLORS } from "components/hoc/Theme";
import type {
  SecureCollectionRow,
  SecureFile,
} from "features/secureUploads/secureUploads";
import useResponsive from "hooks/useResponsive";

import RowMenu, {
  ActionsCell,
  ActionsHeaderCell,
  ROW_BUTTON_PX,
  ROW_ICON_SX,
  type RowMenuItem,
} from "./RowMenu";
import {
  type FileAction,
  type FileSort,
  type FileSortColumn,
  dateTime,
  downloadedByLines,
  fileMenu,
} from "./fileModel";
import { CollectionIcon } from "./icons";
import { locationLabel } from "./uploadModel";

const UNREAD_DOT_COLOR = CALIBRATE_COLORS.iconContainerFill;

export function ConfidentialChip() {
  return (
    <Chip
      label="Confidential"
      size="small"
      sx={{
        height: 18,
        fontSize: 10,
        fontWeight: 600,
        bgcolor: "#FDECEA",
        color: "#C62828",
        flexShrink: 0,
      }}
    />
  );
}

function UnreadDot({ unread }: { unread: boolean }) {
  return (
    <Box
      aria-label={unread ? "Not opened yet" : undefined}
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: unread ? UNREAD_DOT_COLOR : "transparent",
      }}
    />
  );
}

/** The name, bold and dotted until the viewer opens the file; in a search across
 * a project, the collection it sits in below it. */
function FileName({
  file,
  showCollection = false,
}: {
  file: SecureFile;
  showCollection?: boolean;
}) {
  const unread = file.is_unread && !file.is_deleted;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <UnreadDot unread={unread} />
      <InsertDriveFileOutlined
        sx={{ fontSize: 20, color: "text.secondary", flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          title={file.file_name}
          sx={{ fontSize: 14, fontWeight: unread ? 700 : 400, minWidth: 0 }}
        >
          {file.file_name}
        </Typography>
        {showCollection && file.collection_name ? (
          <Typography noWrap sx={{ fontSize: 11, color: "text.secondary" }}>
            In {file.collection_name}
          </Typography>
        ) : null}
      </Box>
      {file.is_confidential ? <ConfidentialChip /> : null}
    </Box>
  );
}

/** A collection: a folder for order, dotted while it holds a file the viewer has
 * not opened. */
function CollectionName({
  collection,
  to,
}: {
  collection: SecureCollectionRow;
  to: string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <UnreadDot unread={collection.has_unread} />
      <MuiLink
        component={Link}
        to={to}
        underline="hover"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          color: UNREAD_DOT_COLOR,
          fontWeight: collection.has_unread ? 700 : 600,
        }}
      >
        <CollectionIcon sx={{ fontSize: 20, flexShrink: 0 }} />
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {collection.name}
        </Box>
      </MuiLink>
    </Box>
  );
}

function DownloadedBy({ file }: { file: SecureFile }) {
  const lines = downloadedByLines(file.downloaded_by);
  if (!lines)
    return (
      <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
        Not yet
      </Typography>
    );
  return (
    <Box sx={{ lineHeight: 1.2 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
        {lines.who}
      </Typography>
      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
        {lines.when}
      </Typography>
    </Box>
  );
}

function AddedBy({ file }: { file: SecureFile }) {
  return file.added_by ? (
    <UserAvatar person={file.added_by} size={26} />
  ) : (
    <Typography sx={{ fontSize: 12, color: "text.disabled" }}>-</Typography>
  );
}

interface Column {
  key: string;
  label: string;
  sort?: FileSortColumn;
  align?: "left" | "center";
  cell: (file: SecureFile) => ReactNode;
}

interface SecureFileTableProps {
  files: SecureFile[];
  /** A project page: its collections, listed before the files. */
  collections?: SecureCollectionRow[];
  collectionPath?: (collection: SecureCollectionRow) => string;
  /** A collection row's menu; empty for whoever cannot change collections. */
  collectionMenu?: (collection: SecureCollectionRow) => RowMenuItem[];
  /** Name the collection under each file (a search across a project). */
  showCollection?: boolean;
  loading: boolean;
  /** Deleted Files: when each was deleted and is purged instead of who has it. */
  deleted?: boolean;
  sort: FileSort | null;
  onSort: (column: FileSortColumn) => void;
  empty: string;
  /** Client View: writes stay visible but off. */
  readOnly: boolean;
  /** Staff who organize the exchange: the move actions. */
  canOrganize?: boolean;
  onAction: (action: FileAction, file: SecureFile) => void;
}

/** The files of a place - Individual Files, a project, a collection or Deleted
 * Files - as a table, or as cards on a narrow screen. */
export default function SecureFileTable({
  files,
  collections = [],
  collectionPath = () => "",
  collectionMenu = () => [],
  showCollection = false,
  loading,
  deleted = false,
  sort,
  onSort,
  empty,
  readOnly,
  canOrganize = false,
  onAction,
}: SecureFileTableProps) {
  const { isCardMode } = useResponsive();

  const columns: Column[] = deleted
    ? [
        {
          key: "name",
          label: "File Name",
          sort: "file_name",
          cell: (f) => <FileName file={f} />,
        },
        {
          key: "from",
          label: "Deleted From",
          cell: (f) => locationLabel(f.project_name, f.collection_name),
        },
        {
          key: "by",
          label: "Deleted By",
          align: "center",
          cell: (f) => f.deleted_by_name || "-",
        },
        {
          key: "deleted",
          label: "Deleted On",
          align: "center",
          cell: (f) => dateTime(f.deleted_at),
        },
        {
          key: "purges",
          label: "Purges On",
          align: "center",
          cell: (f) => dateTime(f.purges_on),
        },
      ]
    : [
        {
          key: "name",
          label: "File Name",
          sort: "file_name",
          cell: (f) => <FileName file={f} showCollection={showCollection} />,
        },
        {
          key: "added",
          label: "Added By",
          sort: "added_by",
          align: "center",
          cell: (f) => <AddedBy file={f} />,
        },
        {
          key: "shared",
          label: "Shared With",
          align: "center",
          cell: (f) => (
            <Box sx={{ display: "inline-flex" }}>
              <AvatarStack people={f.shared_with} size={26} empty="-" />
            </Box>
          ),
        },
        {
          key: "downloaded",
          label: "Downloaded By",
          sort: "downloaded",
          align: "center",
          cell: (f) => <DownloadedBy file={f} />,
        },
      ];

  // A file's download sits before its menu; a collection keeps the place
  // empty, so every row's menu stays in one line.
  const fileActions = (file: SecureFile) => (
    <Box sx={{ display: "inline-flex", alignItems: "center" }}>
      <Tooltip title="Download">
        <IconButton
          size="small"
          aria-label={`Download ${file.file_name}`}
          onClick={() => onAction("download", file)}
          sx={ROW_ICON_SX}
        >
          <FileDownloadOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <RowMenu
        label={`Actions for ${file.file_name}`}
        items={fileMenu(file, readOnly, canOrganize).map((item) => ({
          key: item.action,
          label: item.label,
          danger: item.danger,
          readOnly: item.readOnly,
          dividerBefore: item.action === "delete",
          onClick: () => onAction(item.action, file),
        }))}
      />
    </Box>
  );

  const nothing = files.length === 0 && collections.length === 0;

  const collectionCell = (key: string, collection: SecureCollectionRow) => {
    if (key === "name")
      return (
        <CollectionName
          collection={collection}
          to={collectionPath(collection)}
        />
      );
    if (key === "added")
      return collection.added_by ? (
        <UserAvatar person={collection.added_by} size={26} />
      ) : (
        "-"
      );
    if (key === "shared")
      return (
        <Box sx={{ display: "inline-flex" }}>
          <AvatarStack people={collection.shared_with} size={26} empty="-" />
        </Box>
      );
    return "-";
  };

  const collectionActions = (collection: SecureCollectionRow) => (
    <Box sx={{ display: "inline-flex", alignItems: "center" }}>
      <Box sx={{ width: ROW_BUTTON_PX, flexShrink: 0 }} />
      <RowMenu
        label={`Actions for ${collection.name}`}
        items={collectionMenu(collection)}
      />
    </Box>
  );

  if (isCardMode)
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1.5 }}>
        {loading ? (
          <Skeleton variant="rounded" height={88} sx={{ my: 1.5 }} />
        ) : nothing ? (
          <Typography
            color="text.secondary"
            sx={{ py: 3, textAlign: "center" }}
          >
            {empty}
          </Typography>
        ) : (
          [
            ...collections.map((collection) => (
              <Box
                key={`collection-${collection.id}`}
                sx={{
                  py: 1.25,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {collectionCell("name", collection)}
                </Box>
                {collectionActions(collection)}
              </Box>
            )),
            ...files.map((file) => (
              <Box
                key={file.id}
                sx={{
                  py: 1.25,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, display: "grid", gap: 0.75 }}>
                  <FileName file={file} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      pl: 1.75,
                    }}
                  >
                    <AddedBy file={file} />
                    {deleted ? (
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      >
                        Deleted {dateTime(file.deleted_at)}
                      </Typography>
                    ) : (
                      <DownloadedBy file={file} />
                    )}
                  </Box>
                </Box>
                {fileActions(file)}
              </Box>
            )),
          ]
        )}
      </Box>
    );

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <Table stickyHeader size="small" aria-label="Files">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align ?? "left"}
                sx={{ bgcolor: "grey.100", whiteSpace: "nowrap" }}
              >
                {column.sort ? (
                  <TableSortLabel
                    active={sort?.column === column.sort}
                    direction={
                      sort?.column === column.sort ? sort.direction : "asc"
                    }
                    onClick={() => column.sort && onSort(column.sort)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
            <ActionsHeaderCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? Array.from({ length: 4 }, (_, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      <Skeleton />
                    </TableCell>
                  ))}
                  <ActionsCell />
                </TableRow>
              ))
            : [
                ...collections.map((collection) => (
                  <TableRow key={`collection-${collection.id}`} hover>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align ?? "left"}
                        sx={
                          column.key === "name" ? { maxWidth: 420 } : undefined
                        }
                      >
                        {collectionCell(column.key, collection)}
                      </TableCell>
                    ))}
                    <ActionsCell>{collectionActions(collection)}</ActionsCell>
                  </TableRow>
                )),
                ...files.map((file) => (
                  <TableRow key={file.id} hover>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align ?? "left"}
                        sx={
                          column.key === "name" ? { maxWidth: 420 } : undefined
                        }
                      >
                        {column.cell(file)}
                      </TableCell>
                    ))}
                    <ActionsCell>{fileActions(file)}</ActionsCell>
                  </TableRow>
                )),
              ]}
          {!loading && nothing ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center">
                <Typography color="text.secondary" sx={{ py: 3 }}>
                  {empty}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
